import React, { useContext, useRef } from 'react';

import { useAppSelector } from 'utils/hooks';
import { handleKeyEvents } from 'utils/handlers';

import ui from 'domains/ui';

import ShufflerProduct from './ShufflerProduct';
import { ShufflerContext } from './Shuffler';

import type { ShufflerContextType } from './Shuffler';

type ShufflerProductTileProps = {
  shufflerProduct: ShufflerProduct;
  numIcons: keyof typeof rowHeights;
  height?: number;
  click?: () => void;
};

type RowHeight = { vh: number; min?: number };

const rowHeights: Record<number, RowHeight> = {
  1: { vh: 57 },
  2: { vh: 56, min: 533 },
  3: { vh: 48, min: 427 },
  4: { vh: 40, min: 352 },
  5: { vh: 33, min: 297 },
  6: { vh: 29, min: 260 },
  7: { vh: 26, min: 233 },
  8: { vh: 24, min: 214 },
  9: { vh: 19 },
  10: { vh: 18 },
};

function ShufflerProductTile(props: ShufflerProductTileProps) {
  const {
    shufflerProduct: { id, uid, filterGroup, subcatalog, name, metaData, size, imageUrl },
    numIcons,
    height,
    click,
  } = props;
  const tileElem = useRef<HTMLButtonElement>(null);
  const { itemClick, handleItemNavigation } = useContext(ShufflerContext) as ShufflerContextType;

  const hasMobileNavigation = useAppSelector(ui.selectors.hasMobileNavigation);

  const iconWidth = 100 / numIcons - 2;
  const { vh: rowHeight, min: rowMinHeight } = rowHeights[numIcons];

  const tileHeight = height || (!hasMobileNavigation ? rowHeight : '53');
  const tileMinHeight = !height && (!hasMobileNavigation ? rowMinHeight : '475');

  function handleDecoratedImageMouseEnter() {
    const imageElem = document.querySelector<HTMLImageElement>(`#shuffler-product-${id} img`);
    if (imageElem && imageElem.dataset.oldSrc) {
      const { oldSrc } = imageElem.dataset;
      imageElem.setAttribute('src', oldSrc);
    }
  }

  function handleDecoratedImageMouseLeave() {
    const imageElem = document.querySelector<HTMLImageElement>(`#shuffler-product-${id} img`);
    if (imageElem && imageElem.dataset.newSrc) {
      const { newSrc } = imageElem.dataset;
      imageElem.setAttribute('src', newSrc);
    }
  }

  function handleDecoratedImageLoad() {
    const imageElem = document.querySelector<HTMLImageElement>(`#shuffler-product-${id} img`);
    if (imageElem && imageElem.dataset.noRollover !== 'true') {
      imageElem.addEventListener('mouseenter', handleDecoratedImageMouseEnter);
      imageElem.addEventListener('mouseleave', handleDecoratedImageMouseLeave);
    }
  }

  // broken images (i.e. images where the src doesn't exist) will throw an error
  function handleDecoratedImageError() {
    const imageElem = document.querySelector<HTMLImageElement>(`#shuffler-product-${id} img`);
    if (imageElem) {
      // mark as no-rollover
      imageElem.setAttribute('data-no-rollover', 'true');
      // remove event listeners if any are already set
      imageElem.removeEventListener('mouseenter', handleDecoratedImageMouseEnter);
      imageElem.removeEventListener('mouseleave', handleDecoratedImageMouseLeave);
    }
  }

  // create an image element to load secondary image
  const rolloverImage = document.createElement('img');
  rolloverImage.addEventListener('load', handleDecoratedImageLoad, { once: true });
  rolloverImage.addEventListener('error', handleDecoratedImageError);
  const imageSrc = imageUrl.replace('.png', '_image.jpg');
  rolloverImage.setAttribute('src', imageUrl);

  return (
    <button
      id={`shuffler-product-${id}`}
      ref={tileElem}
      onClick={click || itemClick}
      onKeyDown={handleKeyEvents({ open: itemClick, navigate: handleItemNavigation })}
      className={`invisible catalog-item ${uid} ${filterGroup.split(',').map((styleFilter) => {
        return `style-${styleFilter.replace(/\s+/g, '-')}`;
      })}`}
      key={id}
      data-id={id}
      data-catalog={subcatalog}
      data-groups={`["${subcatalog}"]`}
      data-uid={uid}
      data-size={size}
      data-numphotos={metaData.numphotos}
      style={{
        width: `${!hasMobileNavigation ? iconWidth : '100'}%`,
        height: `${tileHeight}vh`,
        minHeight: `${tileMinHeight || '0'}px`,
        textOverflow: 'ellipsis',
        position: 'relative',
        margin: '0.5vw',
      }}
    >
      <div
        className="aspect"
        style={{
          position: 'relative',
          width: '100%',
          height: '100%',
          paddingBottom: '100%',
          overflow: 'hidden',
        }}
      >
        <div className="aspect__inner" style={{ cursor: 'pointer' }}>
          <div className="item_container animate__flipInX animate__animated">
            <img src={imageSrc} data-new-src={imageSrc} data-old-src={imageUrl} alt={name} />
            <div className="item_details">
              <div className="item_detail_container catalog">
                <span>{subcatalog}</span>
                <span>{size}</span>
              </div>
              <div className="item_detail_container name" title={name}>
                {name}
              </div>
              <div className="item_detail_container meta">
                <span>
                  {metaData.numphotos !== 1 ? 'Up to ' : ''}
                  {metaData.numphotos} Photo{metaData.numphotos !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </button>
  );
}

export default ShufflerProductTile;
